import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import { buildCustomerPayload, emptyCustomerForm } from '../utils/customerForm';
import Modal from './Modal';
import '../pages/Customers.css';

const CreateCustomerModal = ({ isOpen, onClose, onSuccess, initialName = '' }) => {
  const { t } = useTranslation(['customers', 'common']);
  const [formData, setFormData] = useState(emptyCustomerForm);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({ ...emptyCustomerForm, name: initialName || '' });
      setError('');
    }
  }, [isOpen, initialName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setError('');
    setSubmitting(true);

    try {
      const response = await axios.post('/api/customers', buildCustomerPayload(formData));
      onSuccess?.(response.data.data);
      onClose?.();
    } catch (err) {
      setError(formatError(err) || t('customers:failedCreate'));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('customers:createCustomer')}
      className="create-customer-modal"
    >
      {error && <div className="error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="create-customer-name">{t('customers:nameRequired')}</label>
          <input
            id="create-customer-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            maxLength={255}
          />
        </div>
        <div className="form-group">
          <label htmlFor="create-customer-organization">{t('customers:organization')}</label>
          <input
            id="create-customer-organization"
            type="text"
            name="organizationName"
            value={formData.organizationName}
            onChange={handleChange}
            maxLength={255}
          />
        </div>
        <div className="form-group">
          <label htmlFor="create-customer-email">{t('customers:email')}</label>
          <input
            id="create-customer-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            maxLength={255}
          />
        </div>
        <div className="form-group">
          <label htmlFor="create-customer-phone">{t('customers:phone')}</label>
          <input
            id="create-customer-phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            maxLength={50}
          />
        </div>
        <div className="form-group">
          <label htmlFor="create-customer-address">{t('customers:address')}</label>
          <textarea
            id="create-customer-address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            rows={2}
          />
        </div>
        <div className="modal-actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? t('customers:creating') : t('common:create')}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            {t('common:cancel')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateCustomerModal;
